// apps/web/src/lib/prefs-ui.ts — Fase 17.8, Plano 03 (D-02, D-25; T-17.8-03-01/02/03).
//
// Preferências de interface por usuário e por navegador, só em cookie (SEC-001 §13.4 proíbe o armazenamento do
// navegador). O userId entra no valor para que a troca de usuário no mesmo navegador não herde a escolha anterior.
// Tudo que se lê do cookie é entrada não confiável: passa por allowlist de formato antes de chegar à interface, e só
// afeta apresentação (a autorização real continua no servidor). As funções de leitura são puras (recebem a string).

export const COOKIE_ABA_PAINEL = 'pieltec_painel_aba';
export const COOKIE_RECENTES = 'pieltec_recentes';

const MAX_VALOR_COOKIE = 2000;
const MAX_ABA = 40;
const MAX_RECENTES = 5;
const MAX_CAMINHO = 200;
const UM_ANO_EM_SEGUNDOS = 31536000;

/** Valor decodificado do cookie `nome`, ou null se ausente, grande demais ou com escape inválido. */
function lerCookie(cookie: string, nome: string): string | null {
  for (const parte of cookie.split(';')) {
    const texto = parte.trim();
    if (!texto.startsWith(`${nome}=`)) continue;
    const bruto = texto.slice(nome.length + 1);
    if (bruto === '' || bruto.length > MAX_VALOR_COOKIE) return null;
    try {
      return decodeURIComponent(bruto);
    } catch {
      return null;
    }
  }
  return null;
}

function gravarCookie(nome: string, valor: string): void {
  if (typeof document === 'undefined') return;
  const seguro = typeof window !== 'undefined' && window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${nome}=${encodeURIComponent(valor)}; Path=/; Max-Age=${UM_ANO_EM_SEGUNDOS}; SameSite=Lax${seguro}`;
}

/** Aba do painel lembrada para o usuário, ou null (outro usuário, aba fora de `permitidas`, cookie adulterado). */
export function lerAbaPainel(cookie: string, userId: string, permitidas: readonly string[]): string | null {
  const valor = lerCookie(cookie, COOKIE_ABA_PAINEL);
  if (valor === null) return null;
  const separador = valor.indexOf(':');
  if (separador <= 0) return null;
  if (valor.slice(0, separador) !== userId) return null;
  const aba = valor.slice(separador + 1);
  if (aba.length === 0 || aba.length > MAX_ABA) return null;
  return permitidas.includes(aba) ? aba : null;
}

export function gravarAbaPainel(userId: string, aba: string): void {
  gravarCookie(COOKIE_ABA_PAINEL, `${userId}:${aba}`);
}

/** Allowlist de formato: caminho relativo da própria aplicação (começa por '/', sem '//' nem '://', até 200 caracteres). */
export function caminhoValido(caminho: string): boolean {
  return caminho.startsWith('/') && caminho.length <= MAX_CAMINHO && !caminho.includes('//') && !caminho.includes('://');
}

/** Até 5 caminhos recentes do usuário, o mais recente primeiro; entradas fora da allowlist são descartadas. */
export function lerRecentes(cookie: string, userId: string): readonly string[] {
  const valor = lerCookie(cookie, COOKIE_RECENTES);
  if (valor === null) return [];
  const separador = valor.indexOf('|');
  if (separador <= 0 || valor.slice(0, separador) !== userId) return [];
  return valor
    .slice(separador + 1)
    .split(',')
    .filter(caminhoValido)
    .slice(0, MAX_RECENTES);
}

/** Regra de `registrarRecente`: sem query string nem hash, sem duplicar, mais recente primeiro, no máximo 5. */
export function incluirRecente(atuais: readonly string[], caminho: string): readonly string[] {
  const limpo = caminho.split('#')[0]?.split('?')[0] ?? '';
  if (!caminhoValido(limpo)) return atuais;
  return [limpo, ...atuais.filter((c) => c !== limpo)].slice(0, MAX_RECENTES);
}

export function registrarRecente(userId: string, caminho: string): void {
  if (typeof document === 'undefined') return;
  const atuais = lerRecentes(document.cookie, userId);
  const proximos = incluirRecente(atuais, caminho);
  if (proximos === atuais) return;
  gravarCookie(COOKIE_RECENTES, `${userId}|${proximos.join(',')}`);
}
